<script lang="ts">
	import type { FaqBlock } from '$models/Block/FaqBlock';
	import type { Image } from '$models/Image/Image';
	import { page } from '$app/stores';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { jsonLdScriptTag } from '$lib/seo/jsonLd';
	import { buildFaqPageSchema } from '$lib/seo/schemas/faqPage';
	import { sanitizeHtml } from '$lib/utils';
	import { SvelteSet } from 'svelte/reactivity';

	let { block }: { block: FaqBlock } = $props();
	let expandedGroups = new SvelteSet<number>();
	let topics = $derived(block.topics || []);

	function toggleGroup(index: number) {
		expandedGroups.add(index);
	}

	let faqSchemaHtml = $derived.by(() => {
		const schema = buildFaqPageSchema(topics);
		return schema ? jsonLdScriptTag(schema) : '';
	});
</script>

<svelte:head>
	{#if faqSchemaHtml}
		{@html faqSchemaHtml}
	{/if}
</svelte:head>

<div class="faqs">
	<div class={['faqs-container', block.imageAlignment === 'reversed' && 'faqs-container--reverse']}>
		<div class="faqs-main">
			<div class="faqs-header">
				{#if block.title}
					<h2 class="faqs-header-title h4">
						{@html sanitizeHtml(block.title)}
					</h2>
				{/if}
			</div>
			<div class="faqs-list">
				{#each topics as topic, index}
					{@const questions = topic.questions ?? []}
					{@const isExpanded = expandedGroups.has(index)}
					<div class="faqs-group">
						{#if topic.title}
							<h2 class="faqs-title">
								{@html sanitizeHtml(topic.title)}
							</h2>
						{/if}
						<div class="faqs-items">
							{#each questions as question, qIndex}
								<details
									class="faqs-item"
									hidden={questions.length > 5 && !isExpanded && qIndex >= 5}
									open={qIndex === 0}
								>
									<summary class="faqs-toggle">
										{@html sanitizeHtml(question.question)}
									</summary>
									<div class="faqs-text rich-text">
										{@html sanitizeHtml(question.answer)}
									</div>
								</details>
							{/each}
						</div>
						{#if questions.length > 5 && !isExpanded}
							<div class="faqs-footer">
								<button
									class="button button--size-medium button--ghost button--no-arrow"
									onclick={() => toggleGroup(index)}
								>
									{$page.data.page.layout.translations.buttonLabels.viewMoreLabel}
								</button>
							</div>
						{/if}
					</div>
				{/each}
			</div>
		</div>
		{#if block.image}
			<figure class="faqs-image">
				<img
					src={block.image.url}
					srcset={getWordPressImageSrcSet(block.image)}
					sizes="(min-width: 1200px) 450px, (min-width: 768px) 50vw, 100vw"
					alt={block.image.alt}
					width={block.image.width}
					height={block.image.height}
					loading="lazy"
					decoding="async"
				/>
			</figure>
		{/if}
	</div>
</div>

<style lang="postcss">
	.faqs {
		& {
			max-width: var(--container-x-small);
			margin: 0 auto;
		}

		&-container {
			display: grid;
			gap: 24px;

			&--reverse {
				.faqs-main {
					order: 2;
				}

				.faqs-image {
					order: 1;
				}
			}
		}

		&-header {
			&-title {
				font-size: 24px;
			}

			&:not(:last-child) {
				margin-bottom: 24px;
			}
		}

		&-group {
			&:not(:last-child) {
				margin-bottom: 48px;
			}
		}

		&-title {
			&:not(:last-child) {
				margin-bottom: var(--spacing-4);
			}
		}

		&-items {
			&:not(:last-child) {
				margin-bottom: 24px;
			}
		}

		&-item {
			position: relative;
			padding: 22px 0;

			&::after {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}

			&:first-child::before {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				top: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}
		}

		&-footer {
			display: flex;
			justify-content: center;
		}

		&-image {
			img {
				display: block;
				width: 100%;
				height: auto;
				border-radius: 8px;
			}
		}

		@media (--lg) {
			&-container {
				grid-template-columns: minmax(0, 0.5fr) minmax(0, 0.5fr);
				gap: 64px;
			}

			&-main {
				&:only-child {
					grid-column: 1 / -1;
				}
			}

			&-header {
				&-title {
					font-size: 28px;
				}
			}

			&-group {
				&:not(:last-child) {
					margin-bottom: 64px;
				}
			}

			&-items {
				&:not(:last-child) {
					margin-bottom: 32px;
				}
			}

			&-image {
				img {
					border-radius: 16px;
				}
			}
		}

		@media (--xl) {
			&-container {
				grid-template-columns: minmax(0, 0.55fr) minmax(0, 0.45fr);
				gap: 80px;

				&--reverse {
					grid-template-columns: minmax(0, 0.45fr) minmax(0, 0.55fr);
				}
			}
		}
	}
</style>
